import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import SavedSearchSchema, {
  SaveSearchRequestJSON,
  SavedSearchJSON,
  UpdateSavedSearchRequestJSON,
} from "marketplace/saved-searches/_schema/saved-search-schema";
import { z } from "zod";

const savedSearchListTag = { type: "SavedSearch" as const, id: "LIST" };

export const savedSearchApi = api.injectEndpoints({
  endpoints: (build) => ({
    /** The caller's saved searches, newest first. */
    listSavedSearches: build.query<SavedSearchJSON[], void>({
      query: () => ({ url: "marketplace/saved-searches" }),
      rawResponseSchema: z.array(SavedSearchSchema.SavedSearchJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (result) => [
        ...(result?.map(({ id }) => ({ type: "SavedSearch" as const, id })) ?? []),
        savedSearchListTag,
      ],
    }),

    /** One of the caller's saved searches. Another user's answers 403; an unknown id 404. */
    getSavedSearch: build.query<SavedSearchJSON, { savedSearchId: string }>({
      query: ({ savedSearchId }) => ({ url: `marketplace/saved-searches/${savedSearchId}` }),
      rawResponseSchema: SavedSearchSchema.SavedSearchJSONZ,
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { savedSearchId }) => [{ type: "SavedSearch" as const, id: savedSearchId }],
    }),

    saveSearch: build.mutation<SavedSearchJSON, SaveSearchRequestJSON>({
      query: (body) => ({ url: "marketplace/saved-searches", method: "POST", body }),
      rawResponseSchema: SavedSearchSchema.SavedSearchJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [savedSearchListTag],
    }),

    updateSavedSearch: build.mutation<SavedSearchJSON, { savedSearchId: string } & UpdateSavedSearchRequestJSON>({
      query: ({ savedSearchId, ...body }) => ({
        url: `marketplace/saved-searches/${savedSearchId}`,
        method: "PUT",
        body,
      }),
      rawResponseSchema: SavedSearchSchema.SavedSearchJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: (_result, _error, { savedSearchId }) => [
        { type: "SavedSearch" as const, id: savedSearchId },
        savedSearchListTag,
      ],
    }),

    deleteSavedSearch: build.mutation<void, { savedSearchId: string }>({
      query: ({ savedSearchId }) => ({
        url: `marketplace/saved-searches/${savedSearchId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { savedSearchId }) => [
        { type: "SavedSearch" as const, id: savedSearchId },
        savedSearchListTag,
      ],
    }),
  }),
});

export const {
  useListSavedSearchesQuery,
  useGetSavedSearchQuery,
  useSaveSearchMutation,
  useUpdateSavedSearchMutation,
  useDeleteSavedSearchMutation,
} = savedSearchApi;
